import { useBoolean } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import { useUpdateCreditAccountMutation } from "@src/__generated__/graphql";
import { omit } from "lodash";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import type React from "react";
import { type FunctionComponent, useEffect, useState } from "react";
import type { BrandCreditAccountModel as DetailCreditAccountModel } from "../detail/models";
import type { CreditAccountModel } from "../listing/models";
import { getPayloadBudgetCategoryIds, useData } from "./Common";
import { FormCreditAccountModal } from "./FromCreditAccountModal";
import {
  FormCreditAccountCtx,
  StoreCreditAccountForm,
} from "./StoreCreditAccountForm";

export const ModalEditCreditAccount: FunctionComponent<
  React.PropsWithChildren<{
    creditAccount: CreditAccountModel | DetailCreditAccountModel;
    isOpen: boolean;
    onCompleted?: () => void;
    onClose: () => void;
  }>
> = observer(function ModalEditCreditAccount({
  isOpen,
  onClose,
  onCompleted,
  creditAccount,
}) {
  const [store] = useState(() => {
    const store = StoreCreditAccountForm.create();
    return store;
  });
  const [loading, { toggle }] = useBoolean(false);

  const router = useRouter();

  const { data } = useData(store);

  useEffect(() => {
    if (data) {
      store.fillCreditAccountForm(creditAccount);
    }
  }, [data, isOpen]);

  const [updateCreditAccount] = useUpdateCreditAccountMutation();

  const onSubmit = async () => {
    const payload = await store.form.validate();

    if (payload === null) return;
    toggle();

    const { data } = await updateCreditAccount({
      variables: {
        input: {
          id: creditAccount.id,
          ...omit(payload, ["budget_category_ids", "brand_id"]),
          our_work_budget_category_ids: getPayloadBudgetCategoryIds(
            payload,
            store,
            store.workOptions,
          ),
          expense_budget_category_ids: getPayloadBudgetCategoryIds(
            payload,
            store,
            store.expenseOptions,
          ),
        },
      },
    });

    if (onCompleted) {
      onClose();
      onCompleted();
      toggle();
    } else if (data?.updateBrandCreditAccount.id) {
      onClose();
      toggle();
      router.push({
        pathname: "/credit-accounts/detail",
        query: { id: data?.updateBrandCreditAccount.id },
      });
    }
  };

  return (
    <FormCreditAccountCtx.Provider value={store}>
      <FormCreditAccountModal
        isEditForm
        title={t`Edit Credit Account`}
        isOpen={isOpen}
        onClose={onClose}
        primaryAction={{
          title: t`Save changes`,
          action: onSubmit,
          loading: loading,
        }}
      />
    </FormCreditAccountCtx.Provider>
  );
});
